import { validateAddress } from './address-validator';
import { createLogger } from './debug';

const { log } = createLogger('address-validation');

export function attachAddressValidation(formId = 'registration-form') {
  const form      = document.getElementById(formId);
  const modalEl   = document.getElementById('address-modal');
  const verifyBtn = document.getElementById('verify-address-btn');
  log('form:', !!form, '| modal:', !!modalEl, '| verifyBtn:', !!verifyBtn);
  if (!form || !modalEl || !verifyBtn) {
    log('bailing early — one or more elements missing');
    return;
  }
  log('all elements found, attaching handlers');

  const matchSection    = document.getElementById('address-match-section');
  const diffSection     = document.getElementById('address-diff-section');
  const confirmedOutput = document.getElementById('confirmed-address-output');
  const enteredOutput   = document.getElementById('entered-address-output');
  const validatedOutput = document.getElementById('validated-address-output');

  let validatedData = null;
  let enteredData   = null;

  // If the server told us the address was already verified (edit mode, returning user),
  // watch address fields for changes and reset the button if the user edits anything.
  if (verifyBtn.dataset.preVerified === 'true') {
    let resetDone = false;
    const resetVerifyBtn = () => {
      if (resetDone) return;
      resetDone = true;
      verifyBtn.disabled = false;
      verifyBtn.classList.add('hollow');
      verifyBtn.classList.remove('success');
      verifyBtn.innerHTML = '<i class="fa-solid fa-location-dot"></i> Verify Address';
    };
    ['address1', 'address2', 'city', 'state', 'postal_code', 'country'].forEach(name => {
      const input = form.querySelector(`[name="${name}"]`);
      if (input) {
        input.addEventListener('input', resetVerifyBtn);
        input.addEventListener('change', resetVerifyBtn);
      }
    });
  }

  function formatAddress(addr) {
    const { address1 = '', address2 = '', city = '', state = '', postal_code = '' } = addr;
    return `
      <p class="confirmed-address-line">${address1}</p>
      <p class="confirmed-address-line">
        ${address2 ? `${address2}<br>` : ''}
        ${city}, ${state} ${postal_code}
      </p>`;
  }

  function addressesMatch(a, b) {
    const fields = ['address1', 'address2', 'city', 'state', 'postal_code'];
    return fields.every(f =>
      (a[f] || '').trim().toLowerCase() === (b[f] || '').trim().toLowerCase()
    );
  }

  function copyToForm(addr) {
    ['address1', 'address2', 'city', 'state', 'postal_code'].forEach(name => {
      const input = form.querySelector(`[name="${name}"]`);
      if (input) input.value = addr[name] || '';
    });
  }

  function openModal() {
    const trigger = document.createElement('button');
    trigger.setAttribute('data-open', modalEl.id);
    trigger.style.display = 'none';
    document.body.appendChild(trigger);
    trigger.click();
    setTimeout(() => trigger.remove(), 100);
  }

  function closeModal() {
    // Blur any focused element inside the modal before closing to prevent the
    // aria-hidden-on-focused-element accessibility warning from Foundation Reveal.
    if (modalEl.contains(document.activeElement)) {
      document.activeElement.blur();
    }
    const trigger = document.createElement('button');
    trigger.setAttribute('data-close', '');
    trigger.style.display = 'none';
    modalEl.appendChild(trigger);
    trigger.click();
    setTimeout(() => trigger.remove(), 100);
  }

  function markDone(accepted) {
    verifyBtn.disabled = true;
    verifyBtn.classList.remove('hollow');
    if (accepted) {
      verifyBtn.textContent = '\u2713 Address Verified';
      verifyBtn.classList.add('success');
    } else {
      verifyBtn.textContent = '\u2713 Address Confirmed';
    }
  }

  // "Use Verified Address" / "Continue" button — attach to ALL matching buttons
  // (modal has one in match-section and one in diff-section)
  modalEl.querySelectorAll('[data-action="confirm-address"]').forEach(btn => {
    btn.addEventListener('click', () => {
      if (validatedData) copyToForm(validatedData);
      closeModal();
      markDone(true);
    });
  });

  // "Use My Address" button
  modalEl.querySelector('[data-action="use-entered-address"]')?.addEventListener('click', () => {
    closeModal();
    markDone(false);
  });

  // "Verify Address" button click
  verifyBtn.addEventListener('click', async () => {
    log('Verify Address clicked');
    validatedData = null;
    enteredData   = null;

    const payload = Object.fromEntries(new FormData(form).entries());
    log('payload address fields:', {
      address1: payload.address1, city: payload.city, state: payload.state, postal_code: payload.postal_code
    });

    enteredData = {
      address1:    payload.address1    || '',
      address2:    payload.address2    || '',
      city:        payload.city        || '',
      state:       payload.state       || '',
      postal_code: payload.postal_code || '',
    };

    try {
      await validateAddress({
        payload,
        endpoint: '/Address/validate',
        button: verifyBtn,
        onSuccess: (data) => {
          if (!data.validated_address) return;
          validatedData = data.validated_address;

          if (addressesMatch(enteredData, data.validated_address)) {
            matchSection.style.display = 'block';
            diffSection.style.display  = 'none';
            confirmedOutput.innerHTML  = formatAddress(data.validated_address);
          } else {
            matchSection.style.display = 'none';
            diffSection.style.display  = 'block';
            enteredOutput.innerHTML    = formatAddress(enteredData);
            validatedOutput.innerHTML  = formatAddress(data.validated_address);
          }

          const editBtn = modalEl.querySelector('[data-action="edit-address"]');
          if (editBtn) {
            editBtn.style.display = data.remaining_validations > 0 ? '' : 'none';
          }

          const mockNotice = modalEl.querySelector('#mock-mode-notice');
          if (mockNotice) {
            mockNotice.style.display = data.mock ? 'block' : 'none';
          }

          openModal();
        },
        onError: error => {
          if (error.isDisabled) {
            verifyBtn.textContent = 'Verification unavailable';
            verifyBtn.disabled    = true;
            return;
          }
          alert(error.message || 'Could not validate address; please try again.');
        },
      });
    } catch (error) {
      console.error('Unexpected error during address validation:', error);
    }
  });
}
